import { useMemo } from 'react'
import { useCalibrationProfile, useDefaultCalibrationProfile } from '../../hooks/useCalibrationProfiles'
import { RunWithDetails } from '../../types'
import { calculateSegmentPace, formatPace, formatTime } from '../../lib/calculations'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'

interface CalibrationPreviewProps {
  run: RunWithDetails
}

export function CalibrationPreview({ run }: CalibrationPreviewProps) {
  const defaultProfile = useDefaultCalibrationProfile()
  const { data: customProfile } = useCalibrationProfile(run.calibration_profile_id || '')
  
  const activeProfile = run.calibration_profile_id ? customProfile : defaultProfile

  const previewData = useMemo(() => {
    if (!activeProfile || !run.segments?.length) return null

    let cumulativeDistance = 0
    let cumulativeTime = 0

    const segmentCalculations = run.segments.map((segment) => {
      const pace = calculateSegmentPace(segment, activeProfile, cumulativeDistance)
      const movingTime = segment.distance_km * pace
      const totalTime = movingTime + (segment.planned_stop_min || 0)
      
      cumulativeDistance += segment.distance_km
      cumulativeTime += totalTime

      return {
        segment,
        pace,
        movingTime,
        totalTime,
        cumulativeDistance,
        cumulativeTime
      }
    })

    // Calculate how different this is from default
    const defaultCalculations = run.segments.map((segment) => {
      const pace = calculateSegmentPace(segment, defaultProfile, cumulativeDistance)
      return segment.distance_km * pace + (segment.planned_stop_min || 0)
    })
    const defaultTotalTime = defaultCalculations.reduce((sum, time) => sum + time, 0)
    const timeDifference = cumulativeTime - defaultTotalTime

    return {
      segments: segmentCalculations,
      totalDistance: cumulativeDistance,
      totalTime: cumulativeTime,
      timeDifference
    }
  }, [activeProfile, run.segments, defaultProfile])

  if (!activeProfile) {
    return <div className="p-8 text-center text-neutral-600">Loading profile...</div>
  }

  if (!previewData) {
    return (
      <Card>
        <CardContent className="pt-8">
          <p className="text-neutral-500 text-center">
            Add segments to see pace calculations
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-xl font-bold text-primary-900">
            Pace Preview
            <Badge className={run.calibration_profile_id ? "bg-primary-100 text-primary-800" : "bg-neutral-100 text-neutral-700"}>
              {activeProfile.name}
            </Badge>
          </CardTitle>
          <p className="text-sm text-neutral-600">
            Time estimates for each segment using the selected calibration profile
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {previewData.segments.map((calc, index) => (
              <div key={calc.segment.id} className="flex items-center justify-between p-4 bg-neutral-50 rounded-xl border border-neutral-100">
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                    {index + 1}
                  </div>
                  <div>
                    <div className="font-medium text-primary-900">
                      {calc.segment.label || `Segment ${index + 1}`}
                    </div>
                    <div className="text-sm text-neutral-600">
                      {calc.segment.distance_km}km • +{calc.segment.elev_gain_m || 0}m • -{calc.segment.elev_loss_m || 0}m
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono font-semibold text-primary-900">
                    {formatPace(calc.pace)}/km
                  </div>
                  <div className="text-sm text-neutral-600">
                    {formatTime(calc.totalTime)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-primary-50 border-primary-200">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-primary-900">Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-xl border border-primary-100">
              <div className="text-sm text-primary-700 font-medium">Total Distance</div>
              <div className="font-mono text-xl text-primary-900">{previewData.totalDistance.toFixed(1)} km</div>
            </div>
            <div className="bg-white p-4 rounded-xl border border-primary-100">
              <div className="text-sm text-primary-700 font-medium">Total Time</div>
              <div className="font-mono text-xl text-primary-900">{formatTime(previewData.totalTime)}</div>
            </div>
            <div className="bg-white p-4 rounded-xl border border-primary-100">
              <div className="text-sm text-primary-700 font-medium">Average Pace</div>
              <div className="font-mono text-xl text-primary-900">
                {formatPace(previewData.totalTime / previewData.totalDistance)}/km
              </div>
            </div>
            <div className="bg-white p-4 rounded-xl border border-primary-100">
              <div className="text-sm text-primary-700 font-medium">vs Default</div>
              <div className={`font-mono text-xl ${
                Math.abs(previewData.timeDifference) < 1 ? 'text-primary-900' :
                previewData.timeDifference > 0 ? 'text-accent-600' : 'text-emerald-600'
              }`}>
                {previewData.timeDifference > 0 ? '+' : ''}{formatTime(Math.abs(previewData.timeDifference))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-bold text-primary-900">Profile Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex justify-between items-center p-3 bg-neutral-50 rounded-lg">
              <span className="text-neutral-600 font-medium">Base pace:</span>
              <span className="font-mono text-primary-900 font-semibold">{activeProfile.beta0_base_pace_min_per_km.toFixed(2)} min/km</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-neutral-50 rounded-lg">
              <span className="text-neutral-600 font-medium">Uphill factor:</span>
              <span className="font-mono text-primary-900 font-semibold">+{(activeProfile.beta1_uphill_min_per_m_per_km * 100).toFixed(1)}s per 100m/km</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-neutral-50 rounded-lg">
              <span className="text-neutral-600 font-medium">Downhill factor:</span>
              <span className="font-mono text-primary-900 font-semibold">{(activeProfile.beta2_downhill_min_per_m_per_km * 100).toFixed(1)}s per 100m/km</span>
            </div>
            {activeProfile.use_fatigue && (
              <div className="flex justify-between items-center p-3 bg-neutral-50 rounded-lg">
                <span className="text-neutral-600 font-medium">Fatigue factor:</span>
                <span className="font-mono text-primary-900 font-semibold">+{(activeProfile.beta3_fatigue_min_per_km * 1000).toFixed(1)}s per km</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}